/* PerToothPlanAccordion — planner-side "Author the plan" rebuild of the
 * Per-tooth plan section. Single collapsible accordion shell with 4
 * tabs (Movements / Attachments / Precision cuts / Bite ramps), each
 * carrying a tooth chart + inline editor + summary table. Header pills
 * surface live counters for at-a-glance progress.
 *
 * Replaces the legacy four-picker stack (ToothMovementsPicker +
 * AttachmentsPicker + PrecisionCutsPicker + BiteRampsPicker) inside
 * PlanEditorInline. The data shape is identical to the existing plan
 * payload (tooth_movements_plan / attachments_plan / precision_cuts /
 * bite_ramps), so this is a UI-only swap.
 *
 * Theme:
 *   • Movements   — teal     (ep-mv)
 *   • Attachments — purple   (ep-att)
 *   • Cuts        — amber    (ep-cut)
 *   • Bite ramps  — teal toggle
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Label } from "@/components/ui/label";
import {
    Tooth as ToothIcon, PuzzlePiece, Scissors, WaveSine, Trash,
    CaretLeft, CaretRight, XCircle, CheckCircle, ArrowsClockwise,
    Calendar, Ruler, Note, CaretDown, Keyboard, Info, Anchor, Circle,
    Warning,
    ArrowLeft, ArrowRight, ArrowUp, ArrowDown, ArrowsClockwise as RotateCW,
    ArrowsCounterClockwise as RotateCCW, MapPin, Target, Resize, Shapes,
} from "@phosphor-icons/react";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";

/* ─────────── FDI tables (shared) ─────────── */

const ROW_UR = [18, 17, 16, 15, 14, 13, 12, 11];
const ROW_UL = [21, 22, 23, 24, 25, 26, 27, 28];
const ROW_LL = [48, 47, 46, 45, 44, 43, 42, 41];
const ROW_LR = [31, 32, 33, 34, 35, 36, 37, 38];
const UPPER = [...ROW_UR, ...ROW_UL];
const LOWER = [...ROW_LL, ...ROW_LR];
const ALL = [...UPPER, ...LOWER];

const TT = (() => {
    const t = {};
    [18, 17, 16, 28, 27, 26, 38, 37, 36, 48, 47, 46].forEach((n) => (t[n] = "Molar"));
    [15, 14, 24, 25, 34, 35, 44, 45].forEach((n) => (t[n] = "Premolar"));
    [13, 23, 33, 43].forEach((n) => (t[n] = "Canine"));
    [12, 22, 32, 42].forEach((n) => (t[n] = "Lateral"));
    [11, 21, 31, 41].forEach((n) => (t[n] = "Central"));
    return t;
})();

/* ─────────── Movement directions ─────────── */

const DIRS = [
    { id: "Mesial",    legacy: "mesial",       icon: ArrowLeft },
    { id: "Distal",    legacy: "distal",       icon: ArrowRight },
    { id: "Buccal",    legacy: "buccal",       icon: ArrowUp },
    { id: "Lingual",   legacy: "lingual",      icon: ArrowDown },
    { id: "Extrude",   legacy: "extrusion",    icon: ArrowUp },
    { id: "Intrude",   legacy: "intrusion",    icon: ArrowDown },
    { id: "RotateCW",  legacy: "rotation_cw",  icon: RotateCW },
    { id: "RotateCCW", legacy: "rotation_ccw", icon: RotateCCW },
];
const DIR_BY_ID = DIRS.reduce((a, d) => ({ ...a, [d.id]: d }), {});
const LEGACY_TO_DIR = DIRS.reduce((a, d) => ({ ...a, [d.legacy]: d.id }), {});

const ANCHORAGE_OPTS = ["Absolute", "Maximum", "Moderate", "Minimum", "None needed"];

const ATT_SIZES = ["Small", "Medium", "Large"];
const ATT_SHAPES = [
    "Optimised", "Rectangular (horizontal)", "Rectangular (vertical)",
    "Bevelled rectangular", "Ellipsoid", "Root control", "Extrusion", "Rotation wedge",
];
const ATT_PURPOSES = [
    "Torque control", "Rotation", "Extrusion", "Intrusion",
    "Anchorage", "Overcorrection", "Root uprighting",
];

const CUT_PURPOSES = [
    { id: "Elastic hook", short: "Elastic", icon: Anchor },
    { id: "Button", short: "Button", icon: Circle },
    { id: "IPR access", short: "IPR", icon: Scissors },
    { id: "Aligner seat", short: "Seat", icon: null },
];

const BITE_RAMP_TEETH = ["11", "12", "21", "22", "13", "23"];

/* ─────────── Hydrate / serialise helpers ─────────── */

function hydrateMv(legacy) {
    if (!legacy) return null;
    const dirs = (legacy.directions || []).map((d) => LEGACY_TO_DIR[d]).filter(Boolean);
    let torque = "";
    if (typeof legacy.torque_deg === "number") {
        torque = legacy.torque_deg > 0 ? "positive" : legacy.torque_deg < 0 ? "negative" : "signed";
    }
    return {
        tooth: String(legacy.tooth),
        dirs,
        amount: legacy.amount_mm != null ? String(legacy.amount_mm) : "",
        rotation: legacy.rotation_deg != null ? String(legacy.rotation_deg) : "",
        torque,
        anchor: legacy.anchorage || "",
        stage: legacy.start_stage != null ? String(legacy.start_stage) : "",
        notes: legacy.notes || "",
    };
}
function serialiseMv(m) {
    const dirsLegacy = (m.dirs || []).map((id) => DIR_BY_ID[id]?.legacy).filter(Boolean);
    return {
        tooth: String(m.tooth),
        directions: dirsLegacy,
        amount_mm: m.amount === "" ? null : Number(m.amount),
        rotation_deg: m.rotation === "" ? null : Number(m.rotation),
        torque_deg: m.torque === "positive" ? 1 : m.torque === "negative" ? -1 : m.torque === "signed" ? 0 : null,
        anchorage: m.anchor || "",
        notes: m.notes || "",
        start_stage: m.stage === "" ? null : Number(m.stage),
    };
}
function hydrateAtt(p) {
    if (!p) return null;
    return {
        tooth: String(p.tooth),
        size: p.size || "",
        shape: p.shape || "",
        purpose: p.purpose || "",
        stage: p.stage_onset != null ? String(p.stage_onset) : "",
        notes: p.notes || "",
    };
}
function serialiseAtt(m) {
    return {
        tooth: String(m.tooth),
        size: m.size || "",
        shape: m.shape || "",
        purpose: m.purpose || "",
        stage_onset: m.stage === "" ? null : Number(m.stage),
        notes: m.notes || "",
    };
}
function hydrateCut(c) {
    if (!c) return null;
    return {
        tooth: String(c.tooth),
        pos: (c.position || "buccal").toLowerCase() === "lingual" ? "Lingual" : "Buccal",
        purpose: c.purpose || "",
        depth: c.depth_mm != null ? String(c.depth_mm) : "",
        stage: c.stage_onset != null ? String(c.stage_onset) : "",
        notes: c.notes || "",
    };
}
function serialiseCut(c) {
    return {
        tooth: String(c.tooth),
        position: (c.pos || "Buccal").toLowerCase(),
        purpose: c.purpose || "",
        depth_mm: c.depth === "" ? null : Number(c.depth),
        stage_onset: c.stage === "" ? null : Number(c.stage),
        notes: c.notes || "",
    };
}

function emptyMv(n) { return { tooth: String(n), dirs: [], amount: "", rotation: "", torque: "", anchor: "", stage: "", notes: "" }; }
function emptyAtt(n) { return { tooth: String(n), size: "", shape: "", purpose: "", stage: "", notes: "" }; }
function emptyCut(n) { return { tooth: String(n), pos: "Buccal", purpose: "", depth: "", stage: "", notes: "" }; }

function mvState(m) {
    if (!m) return "empty";
    const has = (m.dirs || []).length > 0;
    if (has && m.amount) return "done";
    if (has || m.notes) return "partial";
    return "empty";
}

/* ─────────── Small atoms ─────────── */

function Stepper({ value, onChange, step = 1, min = 0, max = 999, unit, placeholder, testid }) {
    const dec = () => {
        const v = parseFloat(value || 0) - step;
        onChange(Math.max(min, Math.round(v * 100) / 100) || "");
    };
    const inc = () => {
        const v = parseFloat(value || 0) + step;
        onChange(Math.min(max, Math.round(v * 100) / 100));
    };
    return (
        <div className="flex items-center border-[0.5px] border-border rounded-[6px] overflow-hidden bg-card">
            <button type="button" onClick={dec} className="w-7 h-8 bg-muted text-muted-foreground hover:bg-emerald-50 hover:text-emerald-700 text-[15px] font-semibold" data-testid={`${testid}-dec`}>−</button>
            <input
                type="number"
                step={step}
                min={min}
                max={max}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder || "—"}
                className="flex-1 h-8 border-0 outline-none text-center text-[12px] font-semibold bg-transparent px-1"
                data-testid={testid}
            />
            <button type="button" onClick={inc} className="w-7 h-8 bg-muted text-muted-foreground hover:bg-emerald-50 hover:text-emerald-700 text-[15px] font-semibold" data-testid={`${testid}-inc`}>+</button>
            {unit && <span className="text-[10px] text-muted-foreground px-1.5 border-l-[0.5px] border-border h-8 flex items-center bg-muted/40 whitespace-nowrap">{unit}</span>}
        </div>
    );
}

/* Tooth button (size = 46×54 per mockup; smaller than AttachmentsStep). */
function Tooth({ num, status, active, theme, badge, onClick }) {
    const base = "relative w-[46px] h-[54px] rounded-[8px] flex flex-col items-center justify-center gap-0.5 cursor-pointer transition-all select-none shrink-0 border-[1.5px]";
    let cls;
    if (active) {
        cls = `bg-card ${theme.borderActive} -translate-y-[3px] ring-[3px] ${theme.ringActive}`;
    } else if (status === "done") {
        cls = `${theme.bgDone} ${theme.borderDone} hover:-translate-y-[2px] hover:shadow-md`;
    } else if (status === "partial") {
        cls = `${theme.bgPartial || theme.bgDone} ${theme.borderPartial || theme.borderDone} hover:-translate-y-[2px] hover:shadow-md`;
    } else {
        cls = "bg-card border-border hover:border-emerald-600 hover:-translate-y-[2px] hover:shadow-md";
    }
    const numColor = status === "done" || active ? theme.textDone : status === "partial" ? theme.textDone : "text-muted-foreground";
    return (
        <button
            type="button"
            onClick={onClick}
            className={`${base} ${cls}`}
            data-testid={`ptp-${theme.key}-tooth-${num}`}
            data-state={status}
            data-active={active ? "true" : "false"}
            title={`Tooth ${num} — ${TT[num] || ""}`}
        >
            {(status === "done" || status === "partial") && (
                <span className={`absolute top-[3px] right-[4px] w-[6px] h-[6px] rounded-full ${status === "partial" ? theme.dotPartial || theme.dotDone : theme.dotDone}`} />
            )}
            <span className={`text-[11px] font-bold leading-none ${numColor}`}>{num}</span>
            <span className={`text-[7px] uppercase tracking-[0.02em] ${status === "done" ? theme.textType : "text-muted-foreground/70"}`}>{TT[num] || ""}</span>
            {status === "done" && badge && (
                <span className={`absolute bottom-[3px] left-1/2 -translate-x-1/2 text-[7px] font-bold uppercase tracking-[0.02em] px-1 py-[1px] rounded-[3px] ${theme.dotDone} text-white whitespace-nowrap`}>
                    {badge}
                </span>
            )}
        </button>
    );
}

function ToothChart({ byTooth, mvActive, theme, badgeFor, onTap, statusFn }) {
    const rows = [
        { id: "ur", teeth: ROW_UR },
        { id: "ul", teeth: ROW_UL },
        { id: "ll", teeth: ROW_LL },
        { id: "lr", teeth: ROW_LR },
    ];
    // iter98.99ag — Chart surface + arch labels pick up the active tab
    // theme. Default to a neutral muted wash if a theme without chart
    // props is passed (legacy callers).
    const chartBg     = theme?.chartBg     || "bg-muted/40";
    const chartBorder = theme?.chartBorder || "border-border";
    const archLabel   = theme?.archLabel   || "text-muted-foreground/70";
    const archDivider = theme?.archDivider || "bg-border";
    return (
        <div className={`${chartBg} border-[0.5px] ${chartBorder} rounded-[12px] p-3.5`} data-testid={`ptp-${theme.key}-chart`}>
            <div className="flex justify-between items-baseline mb-2.5">
                <span className={`text-[10.5px] font-bold uppercase tracking-[0.12em] ${archLabel}`}>Upper Right <span className="opacity-60 font-semibold">· 1.x</span></span>
                <span className={`text-[10.5px] font-bold uppercase tracking-[0.12em] ${archLabel}`}>Upper Left <span className="opacity-60 font-semibold">· 2.x</span></span>
            </div>
            {rows.slice(0, 2).map((r) => (
                <div key={r.id} className="flex justify-center gap-1 mb-1">
                    {r.teeth.map((n) => (
                        <Tooth
                            key={n}
                            num={n}
                            status={statusFn(byTooth[n])}
                            active={mvActive === n}
                            theme={theme}
                            badge={badgeFor ? badgeFor(byTooth[n]) : null}
                            onClick={() => onTap(n)}
                        />
                    ))}
                </div>
            ))}
            <div className={`h-px ${archDivider} my-2.5 relative`}>
                <span className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${chartBg.replace("/50", "")} px-2.5 text-[9px] font-bold uppercase tracking-[0.12em] ${archLabel} whitespace-nowrap`}>↑ Upper · Lower ↓</span>
            </div>
            {rows.slice(2).map((r) => (
                <div key={r.id} className="flex justify-center gap-1 mb-1">
                    {r.teeth.map((n) => (
                        <Tooth
                            key={n}
                            num={n}
                            status={statusFn(byTooth[n])}
                            active={mvActive === n}
                            theme={theme}
                            badge={badgeFor ? badgeFor(byTooth[n]) : null}
                            onClick={() => onTap(n)}
                        />
                    ))}
                </div>
            ))}
            <div className="flex justify-between items-baseline mt-2.5">
                <span className={`text-[10.5px] font-bold uppercase tracking-[0.12em] ${archLabel}`}>Lower Right <span className="opacity-60 font-semibold">· 4.x</span></span>
                <span className={`text-[10.5px] font-bold uppercase tracking-[0.12em] ${archLabel}`}>Lower Left <span className="opacity-60 font-semibold">· 3.x</span></span>
            </div>
        </div>
    );
}

function ProgRow({ label, done, theme, testid }) {
    const pct = Math.round((done / 16) * 100);
    const archLabel = theme?.archLabel || "text-foreground/80";
    return (
        <div className="flex items-center gap-2.5">
            <span className={`min-w-[64px] text-[10.5px] font-bold uppercase tracking-[0.1em] ${archLabel}`}>{label}</span>
            <div className="flex-1 h-[5px] bg-muted rounded-full overflow-hidden">
                <div className={`h-full ${theme.dotDone} rounded-full transition-all duration-300`} style={{ width: `${pct}%` }} data-testid={`${testid}-fill`} />
            </div>
            <span className={`min-w-[40px] text-right font-bold tabular-nums text-[11px] ${theme.textLabel}`} data-testid={`${testid}-label`}>{done}<span className="opacity-50 font-medium">/16</span></span>
        </div>
    );
}

function ArchProgress({ byTooth, predicate, theme, testidPrefix }) {
    const u = UPPER.filter((n) => predicate(byTooth[n])).length;
    const l = LOWER.filter((n) => predicate(byTooth[n])).length;
    return (
        <div className="grid grid-cols-2 gap-2 mb-3">
            <ProgRow label="Upper arch" done={u} theme={theme} testid={`${testidPrefix}-prog-u`} />
            <ProgRow label="Lower arch" done={l} theme={theme} testid={`${testidPrefix}-prog-l`} />
        </div>
    );
}

function Legend({ items, hint }) {
    return (
        <div className="flex items-center gap-3 flex-wrap py-2 px-3 bg-muted/40 border-[0.5px] border-border rounded-[8px] mb-3 text-[11px] text-muted-foreground">
            {items.map((it, i) => (
                <span key={i} className="flex items-center gap-1.5">
                    <div className={`w-[11px] h-[11px] rounded-[3px] border-[1.5px] ${it.cls}`} />
                    {it.label}
                </span>
            ))}
            {hint && (
                <span className="ml-auto flex items-center gap-1 text-emerald-700 font-medium">
                    <Keyboard size={12} weight="duotone" /> {hint}
                </span>
            )}
        </div>
    );
}

/* ─────────── Themes ─────────── */

const THEMES = {
    mv: {
        key: "mv",
        // iter98.99ag — chart-level surface wash + accent label color so
        // the per-tooth chart picks up the active tab's identity.
        chartBg:     "bg-emerald-50/50",
        chartBorder: "border-emerald-200/70",
        archLabel:   "text-emerald-800",
        archDivider: "bg-emerald-200",
        archChipBg:  "bg-emerald-100/80 text-emerald-800",
        bgDone: "bg-emerald-50",
        borderDone: "border-emerald-400",
        bgPartial: "bg-emerald-50/60",
        borderPartial: "border-emerald-400",
        borderActive: "border-emerald-600",
        ringActive: "ring-emerald-600/20",
        dotDone: "bg-emerald-600",
        dotPartial: "bg-emerald-400",
        textDone: "text-emerald-900",
        textType: "text-emerald-700/75",
        textLabel: "text-emerald-900",
        // editor
        editorBorder: "border-emerald-600",
        editorHeadBg: "bg-emerald-50",
        editorHeadBorder: "border-emerald-400",
        editorTitle: "text-emerald-900",
        editorSub: "text-emerald-700",
        saveBtn: "bg-emerald-600 hover:bg-emerald-700",
        chipSel: "bg-emerald-50 border-emerald-600 text-emerald-900",
        tabClass: "text-emerald-700 border-emerald-600",
        tabCountClass: "bg-emerald-50 text-emerald-900",
        pillClass: "bg-emerald-50 border-emerald-600 text-emerald-900",
    },
    att: {
        key: "att",
        chartBg:     "bg-violet-50/50",
        chartBorder: "border-violet-200/70",
        archLabel:   "text-violet-800",
        archDivider: "bg-violet-200",
        archChipBg:  "bg-violet-100/80 text-violet-800",
        bgDone: "bg-violet-50",
        borderDone: "border-violet-500",
        borderActive: "border-violet-500",
        ringActive: "ring-violet-500/20",
        dotDone: "bg-violet-500",
        textDone: "text-violet-900",
        textType: "text-violet-700/75",
        textLabel: "text-violet-900",
        editorBorder: "border-violet-500",
        editorHeadBg: "bg-violet-50",
        editorHeadBorder: "border-violet-300",
        editorTitle: "text-violet-900",
        editorSub: "text-violet-700",
        saveBtn: "bg-violet-500 hover:bg-violet-600",
        chipSel: "bg-violet-50 border-violet-500 text-violet-900",
        tabClass: "text-violet-700 border-violet-500",
        tabCountClass: "bg-violet-50 text-violet-900",
        pillClass: "bg-violet-50 border-violet-500 text-violet-900",
    },
    cut: {
        key: "cut",
        chartBg:     "bg-amber-50/50",
        chartBorder: "border-amber-200/70",
        archLabel:   "text-amber-800",
        archDivider: "bg-amber-200",
        archChipBg:  "bg-amber-100/80 text-amber-800",
        bgDone: "bg-amber-50",
        borderDone: "border-amber-500",
        borderActive: "border-amber-500",
        ringActive: "ring-amber-500/20",
        dotDone: "bg-amber-500",
        textDone: "text-amber-900",
        textType: "text-amber-900/75",
        textLabel: "text-amber-900",
        editorBorder: "border-amber-500",
        editorHeadBg: "bg-amber-50",
        editorHeadBorder: "border-amber-400",
        editorTitle: "text-amber-900",
        editorSub: "text-amber-900/75",
        saveBtn: "bg-amber-500 hover:bg-amber-600",
        chipSel: "bg-amber-50 border-amber-500 text-amber-900",
        tabClass: "text-amber-700 border-amber-500",
        tabCountClass: "bg-amber-50 text-amber-900",
        pillClass: "bg-amber-50 border-amber-500 text-amber-900",
    },
};

/* ─────────── Editor header / footer ─────────── */

function EditorHeader({ icon: Icon, title, sub, theme, navState, onPrev, onNext, onClose, testidPrefix }) {
    return (
        <div className={`${theme.editorHeadBg} border-b-[0.5px] ${theme.editorHeadBorder} px-4 py-3 flex items-center justify-between`}>
            <div className="flex flex-col gap-0.5">
                <div className={`text-[13px] font-bold ${theme.editorTitle} flex items-center gap-1.5`}>
                    {Icon && <Icon size={14} weight="duotone" />}
                    <span data-testid={`${testidPrefix}-title`}>{title}</span>
                </div>
                <div className={`text-[11px] ${theme.editorSub} opacity-90`} data-testid={`${testidPrefix}-sub`}>{sub}</div>
            </div>
            <div className="flex items-center gap-1.5">
                <button
                    type="button"
                    onClick={onPrev}
                    disabled={!navState.hasPrev}
                    className="w-7 h-7 border-[0.5px] border-border rounded-[6px] bg-card text-muted-foreground flex items-center justify-center hover:bg-muted/60 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`${testidPrefix}-prev`}
                ><CaretLeft size={13} weight="bold" /></button>
                <button
                    type="button"
                    onClick={onNext}
                    disabled={!navState.hasNext}
                    className="w-7 h-7 border-[0.5px] border-border rounded-[6px] bg-card text-muted-foreground flex items-center justify-center hover:bg-muted/60 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`${testidPrefix}-next`}
                ><CaretRight size={13} weight="bold" /></button>
                <button
                    type="button"
                    onClick={onClose}
                    className="w-7 h-7 border-[0.5px] border-border rounded-[6px] bg-card text-muted-foreground flex items-center justify-center hover:bg-muted/60"
                    data-testid={`${testidPrefix}-close`}
                ><XCircle size={12} weight="bold" /></button>
            </div>
        </div>
    );
}

function EditorFooter({ onSave, onClose, onRemove, hasExisting, theme, testidPrefix, removeLabel = "Remove" }) {
    return (
        <div className="border-t-[0.5px] border-border bg-muted/40 px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
                <button
                    type="button"
                    onClick={onSave}
                    className={`${theme.saveBtn} text-white rounded-[6px] px-4 py-1.5 text-[12px] font-semibold flex items-center gap-1.5 transition-colors`}
                    data-testid={`${testidPrefix}-save-next`}
                ><CheckCircle size={13} weight="fill" /> Save &amp; Next</button>
                <button
                    type="button"
                    onClick={onClose}
                    className="text-muted-foreground border-[0.5px] border-border rounded-[6px] px-3 py-1.5 text-[12px] hover:bg-muted/60 transition-colors"
                    data-testid={`${testidPrefix}-done`}
                >Done</button>
            </div>
            {hasExisting && (
                <button
                    type="button"
                    onClick={onRemove}
                    className="text-rose-600 border-[0.5px] border-rose-200 rounded-[6px] px-3 py-1.5 text-[11px] hover:bg-rose-50 transition-colors flex items-center gap-1"
                    data-testid={`${testidPrefix}-remove`}
                ><Trash size={11} weight="duotone" /> {removeLabel}</button>
            )}
        </div>
    );
}

/* ─────────── Movement Editor ─────────── */

function MovementEditor({ tooth, draft, setDraft, navState, onPrev, onNext, onClose, onSave, onRemove, hasExisting }) {
    const theme = THEMES.mv;
    const type = TT[tooth] || "";

    const toggleDir = (id) => {
        setDraft((d) => {
            const has = (d.dirs || []).includes(id);
            return { ...d, dirs: has ? d.dirs.filter((x) => x !== id) : [...(d.dirs || []), id] };
        });
    };

    const preview = useMemo(() => {
        const dirs = (draft.dirs || []).join(" + ");
        const parts = [];
        if (dirs) parts.push(`<strong>${dirs}</strong>`);
        if (draft.amount) parts.push(`${draft.amount}mm`);
        if (draft.rotation) parts.push(`${draft.rotation}°`);
        if (draft.torque) parts.push(`${draft.torque} torque`);
        if (draft.anchor) parts.push(`${draft.anchor} anchorage`);
        if (draft.stage) parts.push(`stage ${draft.stage}`);
        return parts.length > 0 ? parts.join(" · ") : null;
    }, [draft]);

    return (
        <div className={`bg-card border-[1.5px] ${theme.editorBorder} rounded-[12px] mt-3 shadow-md overflow-hidden`} data-testid="ptp-mv-editor">
            <EditorHeader
                icon={ToothIcon}
                title={`Tooth ${tooth} — ${type}`}
                sub={hasExisting ? "Editing movements" : "New — pick directions then magnitudes"}
                theme={theme}
                navState={navState}
                onPrev={onPrev}
                onNext={onNext}
                onClose={onClose}
                testidPrefix="ptp-mv-editor"
            />
            <div className="p-4 bg-card">
                <div className="text-[10px] font-bold uppercase tracking-[0.05em] text-muted-foreground/70 mb-2">Directions</div>
                <div className="grid grid-cols-4 gap-1.5 mb-3">
                    {DIRS.map((d) => {
                        const Icon = d.icon;
                        const on = (draft.dirs || []).includes(d.id);
                        return (
                            <button
                                key={d.id}
                                type="button"
                                onClick={() => toggleDir(d.id)}
                                aria-pressed={on}
                                data-testid={`ptp-mv-dir-${d.id}`}
                                className={[
                                    "flex flex-col items-center justify-center gap-1 px-1 py-2 rounded-[8px] border-[1.5px] text-[10px] font-medium cursor-pointer select-none transition-colors text-center",
                                    on ? "bg-emerald-50 border-emerald-600 text-emerald-900 font-bold" : "bg-card border-border text-muted-foreground hover:bg-emerald-50 hover:border-emerald-600 hover:text-emerald-900",
                                ].join(" ")}
                            >
                                <Icon size={16} weight={on ? "fill" : "regular"} className={on ? "text-emerald-600" : "text-muted-foreground/70"} />
                                <span>{d.id}</span>
                            </button>
                        );
                    })}
                </div>

                <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Amount</Label>
                        <Stepper value={draft.amount} onChange={(v) => setDraft((d) => ({ ...d, amount: String(v) }))} step={0.5} min={0} max={15} unit="mm" testid="ptp-mv-amount" />
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Rotation</Label>
                        <Stepper value={draft.rotation} onChange={(v) => setDraft((d) => ({ ...d, rotation: String(v) }))} step={1} min={0} max={90} unit="°" testid="ptp-mv-rotation" />
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Torque</Label>
                        <div className="flex gap-1">
                            {[
                                { id: "positive", label: "+ Pos" },
                                { id: "signed", label: "Signed" },
                                { id: "negative", label: "− Neg" },
                            ].map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setDraft((d) => ({ ...d, torque: d.torque === t.id ? "" : t.id }))}
                                    className={[
                                        "flex-1 py-1.5 text-[10px] font-medium border-[0.5px] rounded-[6px] cursor-pointer text-center transition-colors",
                                        draft.torque === t.id ? "bg-emerald-50 border-emerald-600 text-emerald-900 font-bold" : "bg-card border-border text-muted-foreground hover:border-emerald-600 hover:text-emerald-700",
                                    ].join(" ")}
                                    data-testid={`ptp-mv-torque-${t.id}`}
                                >{t.label}</button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Anchorage</Label>
                        <select
                            value={draft.anchor}
                            onChange={(e) => setDraft((d) => ({ ...d, anchor: e.target.value }))}
                            className="w-full h-8 border-[0.5px] border-border rounded-[6px] px-2 text-[12px] bg-card outline-none focus:border-emerald-600"
                            data-testid="ptp-mv-anchor"
                        >
                            <option value="">Not specified</option>
                            {ANCHORAGE_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Stage onset</Label>
                        <Stepper value={draft.stage} onChange={(v) => setDraft((d) => ({ ...d, stage: String(v) }))} step={1} min={1} max={60} unit="stage" placeholder="1" testid="ptp-mv-stage" />
                    </div>
                </div>

                <div className="mb-3">
                    <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">
                        Notes <span className="text-muted-foreground/70 font-normal text-[10px] normal-case">(optional)</span>
                    </Label>
                    <textarea
                        value={draft.notes}
                        onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                        placeholder="e.g. Close space mesial first, then derotate…"
                        className="w-full border-[0.5px] border-border rounded-[6px] px-2.5 py-1.5 text-[12px] bg-card outline-none focus:border-emerald-600 resize-y min-h-[48px] leading-snug"
                        data-testid="ptp-mv-notes"
                    />
                </div>

                <div className="text-[9px] font-bold uppercase tracking-[0.05em] text-muted-foreground/70 mb-1">Movement summary</div>
                <div className="bg-muted/40 border-[0.5px] border-border rounded-[6px] px-3 py-2 text-[11px] text-muted-foreground leading-relaxed min-h-[34px]" data-testid="ptp-mv-preview">
                    {preview ? (
                        <>
                            Tooth {tooth}: <span className="text-emerald-900" dangerouslySetInnerHTML={{ __html: preview }} />
                            {draft.notes && (<><br /><span className="italic text-muted-foreground/85">&ldquo;{draft.notes}&rdquo;</span></>)}
                        </>
                    ) : (
                        <span className="italic text-muted-foreground/70">Select directions above to see summary.</span>
                    )}
                </div>
            </div>
            <EditorFooter
                onSave={onSave}
                onClose={onClose}
                onRemove={onRemove}
                hasExisting={hasExisting}
                theme={theme}
                testidPrefix="ptp-mv-editor"
                removeLabel="Clear tooth"
            />
        </div>
    );
}

/* ─────────── Attachment Editor ─────────── */

function AttachmentEditor({ tooth, draft, setDraft, navState, onPrev, onNext, onClose, onSave, onRemove, hasExisting }) {
    const theme = THEMES.att;
    const type = TT[tooth] || "";

    const preview = useMemo(() => {
        const parts = [];
        if (draft.size) parts.push(`<strong>${draft.size}</strong>`);
        if (draft.shape) parts.push(draft.shape);
        if (draft.purpose) parts.push(`for <strong>${draft.purpose}</strong>`);
        if (draft.stage) parts.push(`stage ${draft.stage}`);
        return parts.length > 0 ? parts.join(" · ") : null;
    }, [draft]);

    return (
        <div className={`bg-card border-[1.5px] ${theme.editorBorder} rounded-[12px] mt-3 shadow-md overflow-hidden`} data-testid="ptp-att-editor">
            <EditorHeader
                icon={PuzzlePiece}
                title={`Tooth ${tooth} — ${type}`}
                sub={hasExisting ? "Editing existing attachment" : "New — set size, shape and purpose"}
                theme={theme}
                navState={navState}
                onPrev={onPrev}
                onNext={onNext}
                onClose={onClose}
                testidPrefix="ptp-att-editor"
            />
            <div className="p-4 bg-card">
                <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Size</Label>
                        <div className="flex gap-1 flex-wrap">
                            {ATT_SIZES.map((s) => {
                                const on = draft.size === s;
                                return (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => setDraft((d) => ({ ...d, size: d.size === s ? "" : s }))}
                                        aria-pressed={on}
                                        data-testid={`ptp-att-size-${s}`}
                                        className={[
                                            "inline-flex items-center text-[11px] font-medium px-2.5 py-1 rounded-full border-[1.5px] cursor-pointer transition-all",
                                            on ? `${theme.chipSel} font-bold` : "bg-card border-border text-muted-foreground hover:border-emerald-600",
                                        ].join(" ")}
                                    >{s}</button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Shape</Label>
                        <select
                            value={draft.shape}
                            onChange={(e) => setDraft((d) => ({ ...d, shape: e.target.value }))}
                            className="w-full h-8 border-[0.5px] border-border rounded-[6px] px-2 text-[12px] bg-card outline-none focus:border-violet-500"
                            data-testid="ptp-att-shape"
                        >
                            <option value="">— Select —</option>
                            {ATT_SHAPES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Purpose</Label>
                        <select
                            value={draft.purpose}
                            onChange={(e) => setDraft((d) => ({ ...d, purpose: e.target.value }))}
                            className="w-full h-8 border-[0.5px] border-border rounded-[6px] px-2 text-[12px] bg-card outline-none focus:border-violet-500"
                            data-testid="ptp-att-purpose"
                        >
                            <option value="">— Select —</option>
                            {ATT_PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Stage onset</Label>
                        <Stepper value={draft.stage} onChange={(v) => setDraft((d) => ({ ...d, stage: String(v) }))} step={1} min={1} max={60} unit="stage" placeholder="1" testid="ptp-att-stage" />
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">
                            Notes <span className="text-muted-foreground/70 font-normal text-[10px] normal-case">(optional)</span>
                        </Label>
                        <textarea
                            rows={1}
                            value={draft.notes}
                            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                            placeholder="e.g. Ensure full seat before bonding…"
                            className="w-full border-[0.5px] border-border rounded-[6px] px-2 py-1.5 text-[12px] bg-card outline-none focus:border-violet-500 resize-y min-h-[34px] leading-snug"
                            data-testid="ptp-att-notes"
                        />
                    </div>
                </div>
                <div className="text-[9px] font-bold uppercase tracking-[0.05em] text-muted-foreground/70 mb-1">Preview</div>
                <div className="bg-muted/40 border-[0.5px] border-border rounded-[6px] px-3 py-2 text-[11px] text-muted-foreground leading-relaxed min-h-[34px]" data-testid="ptp-att-preview">
                    {preview ? (
                        <>
                            Tooth {tooth}: <span className="text-violet-900" dangerouslySetInnerHTML={{ __html: preview }} />
                            {draft.notes && (<><br /><span className="italic text-muted-foreground/85">&ldquo;{draft.notes}&rdquo;</span></>)}
                        </>
                    ) : (
                        <span className="italic text-muted-foreground/70">Fill in fields above.</span>
                    )}
                </div>
            </div>
            <EditorFooter onSave={onSave} onClose={onClose} onRemove={onRemove} hasExisting={hasExisting} theme={theme} testidPrefix="ptp-att-editor" />
        </div>
    );
}

/* ─────────── Cut Editor ─────────── */

function CutEditor({ tooth, draft, setDraft, navState, onPrev, onNext, onClose, onSave, onRemove, hasExisting }) {
    const theme = THEMES.cut;
    const type = TT[tooth] || "";

    const preview = useMemo(() => {
        const parts = [`<strong>${draft.pos || "Buccal"}</strong> cut`];
        if (draft.purpose) parts.push(`for <strong>${draft.purpose}</strong>`);
        if (draft.depth) parts.push(`${draft.depth}mm deep`);
        if (draft.stage) parts.push(`stage ${draft.stage}`);
        return parts.join(" · ");
    }, [draft]);

    return (
        <div className={`bg-card border-[1.5px] ${theme.editorBorder} rounded-[12px] mt-3 shadow-md overflow-hidden`} data-testid="ptp-cut-editor">
            <EditorHeader
                icon={Scissors}
                title={`Tooth ${tooth} — ${type}`}
                sub={hasExisting ? "Editing existing cut" : "New cut — defaults to buccal"}
                theme={theme}
                navState={navState}
                onPrev={onPrev}
                onNext={onNext}
                onClose={onClose}
                testidPrefix="ptp-cut-editor"
            />
            <div className="p-4 bg-card">
                <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Position</Label>
                        <div className="flex gap-1.5">
                            {["Buccal", "Lingual"].map((p) => {
                                const on = draft.pos === p;
                                return (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => setDraft((d) => ({ ...d, pos: p }))}
                                        aria-pressed={on}
                                        data-testid={`ptp-cut-pos-${p}`}
                                        className={[
                                            "inline-flex items-center text-[11px] font-medium px-2.5 py-1 rounded-full border-[1.5px] cursor-pointer transition-all",
                                            on ? `${theme.chipSel} font-bold` : "bg-card border-border text-muted-foreground hover:border-emerald-600",
                                        ].join(" ")}
                                    >{p}</button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Purpose</Label>
                        <div className="flex gap-1 flex-wrap">
                            {CUT_PURPOSES.map((p) => {
                                const Icon = p.icon;
                                const on = draft.purpose === p.id;
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => setDraft((d) => ({ ...d, purpose: d.purpose === p.id ? "" : p.id }))}
                                        aria-pressed={on}
                                        data-testid={`ptp-cut-purpose-${p.id.replace(/\s/g, "-")}`}
                                        className={[
                                            "inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full border-[1.5px] cursor-pointer transition-all",
                                            on ? `${theme.chipSel} font-bold` : "bg-card border-border text-muted-foreground hover:border-emerald-600",
                                        ].join(" ")}
                                    >
                                        {Icon && <Icon size={11} weight="regular" />}
                                        {p.short}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Depth</Label>
                        <Stepper value={draft.depth} onChange={(v) => setDraft((d) => ({ ...d, depth: String(v) }))} step={0.25} min={0} max={5} unit="mm" testid="ptp-cut-depth" />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Stage onset</Label>
                        <Stepper value={draft.stage} onChange={(v) => setDraft((d) => ({ ...d, stage: String(v) }))} step={1} min={1} max={60} unit="stage" placeholder="1" testid="ptp-cut-stage" />
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">
                            Notes <span className="text-muted-foreground/70 font-normal text-[10px] normal-case">(optional)</span>
                        </Label>
                        <textarea
                            rows={1}
                            value={draft.notes}
                            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                            placeholder="e.g. Ensure clean cut…"
                            className="w-full border-[0.5px] border-border rounded-[6px] px-2 py-1.5 text-[12px] bg-card outline-none focus:border-amber-500 resize-y min-h-[34px] leading-snug"
                            data-testid="ptp-cut-notes"
                        />
                    </div>
                </div>
                <div className="text-[9px] font-bold uppercase tracking-[0.05em] text-muted-foreground/70 mb-1">Preview</div>
                <div className="bg-muted/40 border-[0.5px] border-border rounded-[6px] px-3 py-2 text-[11px] text-muted-foreground leading-relaxed min-h-[34px]" data-testid="ptp-cut-preview">
                    Tooth {tooth}: <span className="text-amber-900" dangerouslySetInnerHTML={{ __html: preview }} />
                    {draft.notes && (<><br /><span className="italic text-muted-foreground/85">&ldquo;{draft.notes}&rdquo;</span></>)}
                </div>
            </div>
            <EditorFooter onSave={onSave} onClose={onClose} onRemove={onRemove} hasExisting={hasExisting} theme={theme} testidPrefix="ptp-cut-editor" />
        </div>
    );
}

/* ─────────── Summary tables ─────────── */

function MvSummaryTable({ items }) {
    if (items.length === 0) return <div className="text-center text-[11px] text-muted-foreground/70 italic py-3" data-testid="ptp-mv-empty">No movements authored yet. Click a tooth above.</div>;
    return (
        <table className="w-full border-collapse text-[11px]" data-testid="ptp-mv-summary-table">
            <thead><tr className="bg-muted/40 border-b-[0.5px] border-border">
                {["Tooth", "Directions", "Amount", "Rotation", "Torque", "Stage", ""].map((h, i) => (
                    <th key={i} className="text-[9px] font-bold text-muted-foreground/70 uppercase tracking-[0.04em] px-3 py-1.5 text-left">{h}</th>
                ))}
            </tr></thead>
            <tbody>
                {items.map((m) => (
                    <tr key={m.tooth} className="border-b-[0.5px] border-border/60 hover:bg-muted/30">
                        <td className="px-3 py-1.5 font-bold">{m.tooth}</td>
                        <td className="px-3 py-1.5">
                            {(m.dirs || []).length > 0 ? (
                                <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 border-[0.5px] border-emerald-600 text-emerald-900">{m.dirs.join(", ")}</span>
                            ) : <span className="text-muted-foreground/60">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-muted-foreground">{m.amount ? `${m.amount}mm` : "—"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{m.rotation ? `${m.rotation}°` : "—"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{m.torque || "—"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{m.stage ? `Stage ${m.stage}` : "—"}</td>
                        <td className="px-3 py-1.5 text-right">
                            <button onClick={m.onRemove} className="text-muted-foreground/70 hover:text-rose-600 p-0.5" data-testid={`ptp-mv-row-remove-${m.tooth}`}><Trash size={12} weight="duotone" /></button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function AttSummaryTable({ items }) {
    if (items.length === 0) return <div className="text-center text-[11px] text-muted-foreground/70 italic py-3" data-testid="ptp-att-empty">No attachments assigned yet.</div>;
    return (
        <table className="w-full border-collapse text-[11px]" data-testid="ptp-att-summary-table">
            <thead><tr className="bg-muted/40 border-b-[0.5px] border-border">
                {["Tooth", "Size", "Shape", "Purpose", "Stage", ""].map((h, i) => (
                    <th key={i} className="text-[9px] font-bold text-muted-foreground/70 uppercase tracking-[0.04em] px-3 py-1.5 text-left">{h}</th>
                ))}
            </tr></thead>
            <tbody>
                {items.map((m) => (
                    <tr key={m.tooth} className="border-b-[0.5px] border-border/60 hover:bg-muted/30">
                        <td className="px-3 py-1.5 font-bold">{m.tooth}</td>
                        <td className="px-3 py-1.5">{m.size ? <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 border-[0.5px] border-violet-500 text-violet-900">{m.size}</span> : <span className="text-muted-foreground/60">—</span>}</td>
                        <td className="px-3 py-1.5">{m.shape ? <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 border-[0.5px] border-violet-500 text-violet-900">{m.shape}</span> : <span className="text-muted-foreground/60">—</span>}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{m.purpose || "—"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{m.stage ? `Stage ${m.stage}` : "—"}</td>
                        <td className="px-3 py-1.5 text-right"><button onClick={m.onRemove} className="text-muted-foreground/70 hover:text-rose-600 p-0.5" data-testid={`ptp-att-row-remove-${m.tooth}`}><Trash size={12} weight="duotone" /></button></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function CutSummaryTable({ items }) {
    if (items.length === 0) return <div className="text-center text-[11px] text-muted-foreground/70 italic py-3" data-testid="ptp-cut-empty">No precision cuts assigned yet.</div>;
    return (
        <table className="w-full border-collapse text-[11px]" data-testid="ptp-cut-summary-table">
            <thead><tr className="bg-muted/40 border-b-[0.5px] border-border">
                {["Tooth", "Position", "Purpose", "Depth", "Stage", ""].map((h, i) => (
                    <th key={i} className="text-[9px] font-bold text-muted-foreground/70 uppercase tracking-[0.04em] px-3 py-1.5 text-left">{h}</th>
                ))}
            </tr></thead>
            <tbody>
                {items.map((m) => (
                    <tr key={m.tooth} className="border-b-[0.5px] border-border/60 hover:bg-muted/30">
                        <td className="px-3 py-1.5 font-bold">{m.tooth}</td>
                        <td className="px-3 py-1.5">
                            <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 border-[0.5px] border-amber-500 text-amber-900">{m.pos || "Buccal"}</span>
                        </td>
                        <td className="px-3 py-1.5 text-muted-foreground">{m.purpose || "—"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{m.depth ? `${m.depth}mm` : "—"}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{m.stage ? `Stage ${m.stage}` : "—"}</td>
                        <td className="px-3 py-1.5 text-right"><button onClick={m.onRemove} className="text-muted-foreground/70 hover:text-rose-600 p-0.5" data-testid={`ptp-cut-row-remove-${m.tooth}`}><Trash size={12} weight="duotone" /></button></td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/* ─────────── Tabs ─────────── */

/* iter98.99q — Per-tooth tab nav redesigned from a flat text-link row
   into a vivid 4-card grid. The previous design rendered the tabs as
   muted underlined text that doctors routinely missed — they walked
   past the bite ramps + precision cuts authoring surfaces without
   ever clicking in. The new pattern surfaces each tab as a tinted
   card with:
     · A colored icon tile (matches THEMES accent of that tab)
     · The label in font-display + emphasized weight
     · A live "X authored" badge in the same accent
     · A clear ▸ chevron + "Tap to author" sub-line so the affordance
       is obvious even before any data is captured
   Active tab gets a 4px colored top-bar + a soft tinted background +
   shadow lift to read as "you are here". Hover lifts inactive cards
   so the entire surface reads as an interactive grid. */
function TabNav({ active, counts, onSwitch }) {
    /* iter98.99s — Tab labels shortened so they fit on ONE line inside
       each card. The descriptive sub-line below now carries the longer
       phrasing ("Tooth-by-tooth · Author the prescribed movement…"). */
    const tabs = [
        { id: "mv",   label: "Movements",  icon: ToothIcon,   count: counts.mv,  theme: THEMES.mv,  accent: "emerald", sub: "Tooth-by-tooth · author the prescribed movement for each tooth" },
        { id: "att",  label: "Attachments", icon: PuzzlePiece, count: counts.att, theme: THEMES.att, accent: "violet",  sub: "Placement · type / stage / shape per tooth" },
        { id: "cut",  label: "Precision cuts", icon: Scissors, count: counts.cut, theme: THEMES.cut, accent: "amber",   sub: "Elastics windows + buccal slots per tooth" },
        { id: "bite", label: "Bite ramps", icon: WaveSine,    count: null,       theme: THEMES.mv,  accent: "rose",    sub: "Position + onset stage + height" },
    ];

    const ACCENT = {
        emerald: {
            cardActive: "border-emerald-300 bg-gradient-to-br from-emerald-50 to-white ring-1 ring-emerald-200/70 shadow-sm",
            cardIdle:   "border-border bg-card hover:border-emerald-300 hover:bg-emerald-50/40 hover:shadow-sm",
            topBar:     "bg-emerald-600",
            iconTile:   "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200",
            label:      "text-emerald-900",
            sub:        "text-emerald-700/80",
            countActive: "bg-emerald-600 text-white",
            countIdle:   "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200",
            chevron:     "text-emerald-500",
            empty:       "text-emerald-700/60",
        },
        violet: {
            cardActive: "border-violet-300 bg-gradient-to-br from-violet-50 to-white ring-1 ring-violet-200/70 shadow-sm",
            cardIdle:   "border-border bg-card hover:border-violet-300 hover:bg-violet-50/40 hover:shadow-sm",
            topBar:     "bg-violet-600",
            iconTile:   "bg-violet-100 text-violet-700 ring-1 ring-violet-200",
            label:      "text-violet-900",
            sub:        "text-violet-700/80",
            countActive: "bg-violet-600 text-white",
            countIdle:   "bg-violet-50 text-violet-800 ring-1 ring-violet-200",
            chevron:     "text-violet-500",
            empty:       "text-violet-700/60",
        },
        amber: {
            cardActive: "border-amber-300 bg-gradient-to-br from-amber-50 to-white ring-1 ring-amber-200/70 shadow-sm",
            cardIdle:   "border-border bg-card hover:border-amber-300 hover:bg-amber-50/40 hover:shadow-sm",
            topBar:     "bg-amber-500",
            iconTile:   "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
            label:      "text-amber-900",
            sub:        "text-amber-700/80",
            countActive: "bg-amber-500 text-white",
            countIdle:   "bg-amber-50 text-amber-800 ring-1 ring-amber-200",
            chevron:     "text-amber-500",
            empty:       "text-amber-700/60",
        },
        rose: {
            cardActive: "border-rose-300 bg-gradient-to-br from-rose-50 to-white ring-1 ring-rose-200/70 shadow-sm",
            cardIdle:   "border-border bg-card hover:border-rose-300 hover:bg-rose-50/40 hover:shadow-sm",
            topBar:     "bg-rose-500",
            iconTile:   "bg-rose-100 text-rose-600 ring-1 ring-rose-200",
            label:      "text-rose-900",
            sub:        "text-rose-700/80",
            countActive: "bg-rose-500 text-white",
            countIdle:   "bg-rose-50 text-rose-800 ring-1 ring-rose-200",
            chevron:     "text-rose-500",
            empty:       "text-rose-700/60",
        },
    };

    return (
        <div className="px-4 pt-4 pb-3 bg-muted/30 border-b-[0.5px] border-border" data-testid="ptp-tab-nav">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-3">
                Author the per-tooth plan · pick a section to start
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                {tabs.map((t) => {
                    const Icon = t.icon;
                    const on = active === t.id;
                    const a = ACCENT[t.accent];
                    // iter98.99ad — Per-tooth tab cards rebuilt with minimum
                    // text per user direction. The wordy "▸ Tap to start —
                    // Tooth-by-tooth · author..." subtitle paragraph removed
                    // (it was being truncated anyway and the panel that
                    // mounts after click already shows a one-line helper).
                    // New shape: compact horizontal row — icon · label ·
                    // count pill. Active tab carries a thin accent bar on
                    // the LEFT edge (not the top) so the rhythm reads as
                    // a row of tabs, not a row of unrelated tiles.
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => onSwitch(t.id)}
                            className={[
                                "relative text-left rounded-xl border px-3 py-2.5 transition-all overflow-hidden flex items-center gap-2.5",
                                on ? a.cardActive : a.cardIdle,
                            ].join(" ")}
                            data-testid={`ptp-tab-${t.id}`}
                            aria-pressed={on}
                        >
                            {/* Left accent stripe — visible when active. */}
                            {on && (
                                <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${a.topBar}`} aria-hidden="true" />
                            )}
                            {/* Icon tile */}
                            <div className={`shrink-0 w-8 h-8 rounded-lg grid place-items-center ${a.iconTile}`}>
                                <Icon size={15} weight="duotone" />
                            </div>
                            {/* Label + count */}
                            <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                                <span className={`font-display text-[13.5px] font-semibold leading-none ${a.label} truncate`}>
                                    {t.label}
                                </span>
                                {t.count != null && (
                                    <span
                                        className={[
                                            "shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums",
                                            on ? a.countActive : a.countIdle,
                                        ].join(" ")}
                                        data-testid={`ptp-tab-${t.id}-count`}
                                    >
                                        {t.count}
                                    </span>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/* ─────────── Bite Ramps panel ─────────── */
/* iter97.59 — toggle removed at user direction. Since this is now a tab in
 * the per-tooth accordion the gate is redundant; planners (and doctors,
 * mirrored in AttachmentsStep) just fill the fields when they apply.
 * `enabled` is implicitly true whenever the planner has chosen at least
 * one target tooth, stage, or ramp height — that drives the "Ramps on"
 * pill in the accordion header. */

function BiteRampsPanel({ value, onChange }) {
    const teeth = value?.teeth || [];
    const stage = value?.stage_onset != null ? String(value.stage_onset) : "";
    const height = value?.ramp_height_mm != null ? String(value.ramp_height_mm) : "";
    const notes = value?.notes || "";

    // Persisted enabled flag is derived from whether the planner has provided
    // any meaningful value (teeth / stage / height). Notes alone aren't
    // enough — they could be left over from a deleted plan.
    const deriveEnabled = (next) => (
        (next.teeth && next.teeth.length > 0)
        || next.stage_onset != null
        || next.ramp_height_mm != null
    );

    const patch = (p) => {
        const next = { ...(value || {}), position: value?.position || "custom", ...p };
        next.enabled = deriveEnabled(next);
        onChange(next);
    };
    const toggleTooth = (n) => {
        const has = teeth.includes(n);
        patch({ teeth: has ? teeth.filter((x) => x !== n) : [...teeth, n] });
    };

    return (
        <div data-testid="ptp-bite-panel">
            <div className="pb-2">
                <div className="text-[13px] font-semibold flex items-center gap-1.5">
                    <WaveSine size={14} weight="duotone" className="text-emerald-600" />
                    Bite ramps
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Optional — for deep-bite correction cases only.</div>
            </div>
            <div className="pt-3" data-testid="ptp-bite-body">
                <div className="text-[10px] font-bold uppercase tracking-[0.05em] text-muted-foreground/70 mb-1.5">Target teeth</div>
                <div className="flex gap-1.5 flex-wrap mb-3.5">
                    {BITE_RAMP_TEETH.map((n) => {
                        const on = teeth.includes(n);
                        return (
                            <button
                                key={n}
                                type="button"
                                onClick={() => toggleTooth(n)}
                                aria-pressed={on}
                                data-testid={`ptp-bite-tooth-${n}`}
                                className={[
                                    "inline-flex items-center gap-1 text-[11px] font-medium px-3 py-1 rounded-full border-[1.5px] cursor-pointer transition-all",
                                    on ? "bg-emerald-50 border-emerald-600 text-emerald-900 font-bold" : "bg-card border-border text-muted-foreground hover:border-emerald-600",
                                ].join(" ")}
                            ><ToothIcon size={11} weight="duotone" /> {n}</button>
                        );
                    })}
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Stage onset</Label>
                        <Stepper value={stage} onChange={(v) => patch({ stage_onset: v === "" ? null : Number(v) })} step={1} min={1} max={60} unit="stage" placeholder="1" testid="ptp-bite-stage" />
                    </div>
                    <div>
                        <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">Ramp height</Label>
                        <Stepper value={height} onChange={(v) => patch({ ramp_height_mm: v === "" ? null : Number(v) })} step={0.5} min={0} max={10} unit="mm" testid="ptp-bite-height" />
                    </div>
                </div>

                <div className="mb-3">
                    <Label className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground mb-1.5 block">
                        Notes <span className="text-muted-foreground/70 font-normal text-[10px] normal-case">(optional)</span>
                    </Label>
                    <textarea
                        value={notes}
                        onChange={(e) => patch({ notes: e.target.value })}
                        placeholder="e.g. Ramps on 11, 21 from stage 1. Remove at stage 12 when deep bite corrected."
                        className="w-full border-[0.5px] border-border rounded-[6px] px-2.5 py-1.5 text-[12px] bg-card outline-none focus:border-emerald-600 resize-y min-h-[52px] leading-snug"
                        data-testid="ptp-bite-notes"
                    />
                </div>

                <div className="flex items-start gap-1.5 p-2.5 rounded-[8px] text-[11px] leading-snug bg-emerald-50 border-[0.5px] border-emerald-400 text-emerald-900">
                    <Info size={13} weight="duotone" className="mt-0.5 shrink-0" />
                    <p>Bite ramps are bonded to upper incisors to open vertical dimension. Typically used in deep-bite correction from stage 1.</p>
                </div>
            </div>
        </div>
    );
}

/* ─────────── Main component ─────────── */

/* iter97.58 — When mounted inside PlanEditorInline's section accordion list
 * the parent owns open/closed state, the section number, the anchor id,
 * the completion summary, and the "Next" advancement. We accept those as
 * optional props so the accordion can be controlled from outside; if
 * they aren't supplied (e.g. standalone preview), an internal default
 * keeps the component self-contained.
 *
 * iter97.61 — `headless` prop strips the accordion shell entirely (no
 * trigger header, no section number, no chevron, no Next footer). Used
 * on the doctor's case-submission wizard Step 4, where the wizard step
 * itself bounds the section so an extra accordion shell would be a
 * redundant double-frame. The 4 tabs (Movements · Attachments ·
 * Precision Cuts · Bite Ramps) render directly. */
export default function PerToothPlanAccordion({
    plan,
    onPlanChange,
    number = 4,
    anchorId = null,
    isOpen: controlledOpen = null,
    onToggle = null,
    onNext = null,
    isComplete = false,
    summary = "",
    headless = false,
    /* iter98.99n — Accent left-bar so this accordion visually
       matches the other Author-the-plan sections (treatment-plan
       output → emerald). Defaults to emerald since per-tooth detail
       is fundamentally a treatment-plan-stage decision. */
    accent = "emerald",
}) {
    const rawMv = plan.tooth_movements_plan || [];
    const rawAtt = plan.attachments_plan || [];
    const rawCut = plan.precision_cuts || [];
    const bite = plan.bite_ramps || { enabled: false };

    const byMv = useMemo(() => {
        const m = {};
        rawMv.forEach((p) => { const ui = hydrateMv(p); if (ui) m[Number(ui.tooth)] = ui; });
        return m;
    }, [rawMv]);
    const byAtt = useMemo(() => {
        const m = {};
        rawAtt.forEach((p) => { const ui = hydrateAtt(p); if (ui) m[Number(ui.tooth)] = ui; });
        return m;
    }, [rawAtt]);
    const byCut = useMemo(() => {
        const m = {};
        rawCut.forEach((p) => { const ui = hydrateCut(p); if (ui) m[Number(ui.tooth)] = ui; });
        return m;
    }, [rawCut]);

    /* ─── Accordion + tab state ─── */
    /* iter97.58 — accordion openness is controlled by parent in plan-editor
     * mode (controlledOpen + onToggle). Otherwise we manage our own state.
     * iter98.99dd — Default collapsed per user instruction. */
    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen != null ? controlledOpen : internalOpen;
    const toggleOpen = onToggle || (() => setInternalOpen((x) => !x));
    const [tab, setTab] = useState("mv");

    /* ─── Movement editor ─── */
    const [mvActive, setMvActive] = useState(null);
    const [mvDraft, setMvDraft] = useState(null);
    const writeMvDraft = useCallback((tooth, d) => {
        const next = serialiseMv({ ...d, tooth });
        const others = rawMv.filter((m) => String(m.tooth) !== String(tooth));
        const hasAny = (d.dirs || []).length > 0 || d.amount || d.rotation || d.notes;
        onPlanChange("tooth_movements_plan", hasAny ? [...others, next] : others);
    }, [rawMv, onPlanChange]);
    const openMv = useCallback((n) => { setMvActive(n); const ex = byMv[n]; setMvDraft(ex ? { ...ex } : emptyMv(n)); }, [byMv]);
    const closeMv = useCallback(() => { setMvActive(null); setMvDraft(null); }, []);
    const navMv = useCallback((dir) => {
        if (mvActive == null || !mvDraft) return;
        writeMvDraft(mvActive, mvDraft);
        const idx = ALL.indexOf(mvActive);
        const next = idx + dir;
        if (next >= 0 && next < ALL.length) {
            const t = ALL[next];
            setMvActive(t);
            const ex = byMv[t];
            setMvDraft(ex ? { ...ex } : emptyMv(t));
        }
    }, [mvActive, mvDraft, byMv, writeMvDraft]);
    const saveMvAndNext = useCallback(() => {
        if (mvActive == null || !mvDraft) return;
        if ((mvDraft.dirs || []).length === 0 && !mvDraft.amount && !mvDraft.notes) {
            alertDialog({ title: "Please select at least one direction.", tone: "warn" });
            return;
        }
        writeMvDraft(mvActive, mvDraft);
        const idx = ALL.indexOf(mvActive);
        if (idx < ALL.length - 1) {
            const t = ALL[idx + 1];
            setMvActive(t);
            const ex = byMv[t];
            setMvDraft(ex ? { ...ex } : emptyMv(t));
        } else closeMv();
    }, [mvActive, mvDraft, byMv, writeMvDraft, closeMv]);
    const removeMv = useCallback(() => {
        if (mvActive == null) return;
        onPlanChange("tooth_movements_plan", rawMv.filter((m) => String(m.tooth) !== String(mvActive)));
        closeMv();
    }, [mvActive, rawMv, onPlanChange, closeMv]);
    const removeMvByTooth = useCallback((n) => onPlanChange("tooth_movements_plan", rawMv.filter((m) => String(m.tooth) !== String(n))), [rawMv, onPlanChange]);

    /* ─── Attachment editor ─── */
    const [attActive, setAttActive] = useState(null);
    const [attDraft, setAttDraft] = useState(null);
    const writeAttDraft = useCallback((tooth, d) => {
        const next = serialiseAtt({ ...d, tooth });
        const others = rawAtt.filter((m) => String(m.tooth) !== String(tooth));
        const hasAny = d.size || d.shape || d.purpose || d.notes;
        onPlanChange("attachments_plan", hasAny ? [...others, next] : others);
    }, [rawAtt, onPlanChange]);
    const openAtt = useCallback((n) => { setAttActive(n); const ex = byAtt[n]; setAttDraft(ex ? { ...ex } : emptyAtt(n)); }, [byAtt]);
    const closeAtt = useCallback(() => { setAttActive(null); setAttDraft(null); }, []);
    const navAtt = useCallback((dir) => {
        if (attActive == null || !attDraft) return;
        writeAttDraft(attActive, attDraft);
        const idx = ALL.indexOf(attActive);
        const next = idx + dir;
        if (next >= 0 && next < ALL.length) {
            const t = ALL[next];
            setAttActive(t);
            const ex = byAtt[t];
            setAttDraft(ex ? { ...ex } : emptyAtt(t));
        }
    }, [attActive, attDraft, byAtt, writeAttDraft]);
    const saveAttAndNext = useCallback(() => {
        if (attActive == null || !attDraft) return;
        if (!attDraft.size && !attDraft.shape) {
            alertDialog({ title: "Set at least a size or shape.", tone: "warn" });
            return;
        }
        writeAttDraft(attActive, attDraft);
        const idx = ALL.indexOf(attActive);
        if (idx < ALL.length - 1) {
            const t = ALL[idx + 1];
            setAttActive(t);
            const ex = byAtt[t];
            setAttDraft(ex ? { ...ex } : emptyAtt(t));
        } else closeAtt();
    }, [attActive, attDraft, byAtt, writeAttDraft, closeAtt]);
    const removeAtt = useCallback(() => {
        if (attActive == null) return;
        onPlanChange("attachments_plan", rawAtt.filter((m) => String(m.tooth) !== String(attActive)));
        closeAtt();
    }, [attActive, rawAtt, onPlanChange, closeAtt]);
    const removeAttByTooth = useCallback((n) => onPlanChange("attachments_plan", rawAtt.filter((m) => String(m.tooth) !== String(n))), [rawAtt, onPlanChange]);
    const clearAllAtt = useCallback(async () => {
        if (rawAtt.length === 0) return;
        if (!(await confirmDialog({ title: "Clear all attachments?", confirmText: "Clear all", tone: "danger" }))) return;
        onPlanChange("attachments_plan", []);
        closeAtt();
    }, [rawAtt.length, onPlanChange, closeAtt]);

    /* ─── Cut editor ─── */
    const [cutActive, setCutActive] = useState(null);
    const [cutDraft, setCutDraft] = useState(null);
    const writeCutDraft = useCallback((tooth, d) => {
        const next = serialiseCut({ ...d, tooth });
        const others = rawCut.filter((m) => String(m.tooth) !== String(tooth));
        onPlanChange("precision_cuts", [...others, next]);
    }, [rawCut, onPlanChange]);
    const openCut = useCallback((n) => { setCutActive(n); const ex = byCut[n]; setCutDraft(ex ? { ...ex } : emptyCut(n)); }, [byCut]);
    const closeCut = useCallback(() => { setCutActive(null); setCutDraft(null); }, []);
    const navCut = useCallback((dir) => {
        if (cutActive == null || !cutDraft) return;
        writeCutDraft(cutActive, cutDraft);
        const idx = ALL.indexOf(cutActive);
        const next = idx + dir;
        if (next >= 0 && next < ALL.length) {
            const t = ALL[next];
            setCutActive(t);
            const ex = byCut[t];
            setCutDraft(ex ? { ...ex } : emptyCut(t));
        }
    }, [cutActive, cutDraft, byCut, writeCutDraft]);
    const saveCutAndNext = useCallback(() => {
        if (cutActive == null || !cutDraft) return;
        writeCutDraft(cutActive, cutDraft);
        const idx = ALL.indexOf(cutActive);
        if (idx < ALL.length - 1) {
            const t = ALL[idx + 1];
            setCutActive(t);
            const ex = byCut[t];
            setCutDraft(ex ? { ...ex } : emptyCut(t));
        } else closeCut();
    }, [cutActive, cutDraft, byCut, writeCutDraft, closeCut]);
    const removeCut = useCallback(() => {
        if (cutActive == null) return;
        onPlanChange("precision_cuts", rawCut.filter((m) => String(m.tooth) !== String(cutActive)));
        closeCut();
    }, [cutActive, rawCut, onPlanChange, closeCut]);
    const removeCutByTooth = useCallback((n) => onPlanChange("precision_cuts", rawCut.filter((m) => String(m.tooth) !== String(n))), [rawCut, onPlanChange]);
    const clearAllCuts = useCallback(async () => {
        if (rawCut.length === 0) return;
        if (!await confirmDialog({ title: "Clear all precision cuts?", confirmText: "Confirm", tone: "danger" })) return;
        onPlanChange("precision_cuts", []);
        closeCut();
    }, [rawCut.length, onPlanChange, closeCut]);

    /* ─── Bite ramps ─── */
    const setBite = useCallback((v) => onPlanChange("bite_ramps", v), [onPlanChange]);

    /* ─── Live draft persistence ─── */
    /* iter98.69 — Was an infinite-loop trap.
     *
     * Bug: when the planner clicked a tooth, the editor would crash
     * with "Maximum update depth exceeded" → ErrorBoundary showed
     * "We couldn't load this page".
     *
     * Root cause: these three effects depended on `writeMvDraft` /
     * `writeAttDraft` / `writeCutDraft`, each of which is a useCallback
     * with `rawMv` / `rawAtt` / `rawCut` + `onPlanChange` in its dep
     * array. Every call to `writeMvDraft` invokes `onPlanChange`,
     * which triggers `setPlan` on the parent (PlanEditorInline),
     * which re-renders and produces a NEW `upd` function reference
     * (it's not wrapped in useCallback). That new `onPlanChange`
     * reference flowed back here and forced the `useCallback` to
     * mint a fresh `writeMvDraft`, which the effect's deps caught as
     * "changed" → effect re-fires → calls `writeMvDraft` again →
     * infinite loop.
     *
     * Fix: stash the latest writer functions in refs and depend ONLY
     * on the actual data (mvDraft / mvActive). The ref always holds
     * the freshest closure, so behavior is identical, but the effect
     * no longer re-fires on parent re-renders. */
    const writeMvDraftRef = useRef(writeMvDraft);
    const writeAttDraftRef = useRef(writeAttDraft);
    const writeCutDraftRef = useRef(writeCutDraft);
    useEffect(() => { writeMvDraftRef.current = writeMvDraft; });
    useEffect(() => { writeAttDraftRef.current = writeAttDraft; });
    useEffect(() => { writeCutDraftRef.current = writeCutDraft; });
    useEffect(() => { if (mvActive != null && mvDraft) writeMvDraftRef.current(mvActive, mvDraft); }, [mvDraft, mvActive]);
    useEffect(() => { if (attActive != null && attDraft) writeAttDraftRef.current(attActive, attDraft); }, [attDraft, attActive]);
    useEffect(() => { if (cutActive != null && cutDraft) writeCutDraftRef.current(cutActive, cutDraft); }, [cutDraft, cutActive]);

    /* ─── Editor scroll-into-view ─── */
    useEffect(() => { if (mvActive == null) return; const el = document.querySelector('[data-testid="ptp-mv-editor"]'); el?.scrollIntoView?.({ behavior: "smooth", block: "nearest" }); }, [mvActive]);
    useEffect(() => { if (attActive == null) return; const el = document.querySelector('[data-testid="ptp-att-editor"]'); el?.scrollIntoView?.({ behavior: "smooth", block: "nearest" }); }, [attActive]);
    useEffect(() => { if (cutActive == null) return; const el = document.querySelector('[data-testid="ptp-cut-editor"]'); el?.scrollIntoView?.({ behavior: "smooth", block: "nearest" }); }, [cutActive]);

    /* ─── Keyboard nav per active editor ─── */
    useEffect(() => {
        const handler = (e) => {
            const tag = (e.target?.tagName || "").toUpperCase();
            if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) return;
            if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
            const dir = e.key === "ArrowRight" ? 1 : -1;
            if (mvActive != null) { e.preventDefault(); navMv(dir); }
            else if (attActive != null) { e.preventDefault(); navAtt(dir); }
            else if (cutActive != null) { e.preventDefault(); navCut(dir); }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [mvActive, attActive, cutActive, navMv, navAtt, navCut]);

    const mvAuthored = Object.values(byMv).filter((m) => (m.dirs || []).length > 0).length;
    const attCount = Object.keys(byAtt).length;
    const cutCount = Object.keys(byCut).length;
    const biteOn = !!bite?.enabled;

    const mvNavState = useMemo(() => { if (mvActive == null) return { hasPrev: false, hasNext: false }; const i = ALL.indexOf(mvActive); return { hasPrev: i > 0, hasNext: i < ALL.length - 1 }; }, [mvActive]);
    const attNavState = useMemo(() => { if (attActive == null) return { hasPrev: false, hasNext: false }; const i = ALL.indexOf(attActive); return { hasPrev: i > 0, hasNext: i < ALL.length - 1 }; }, [attActive]);
    const cutNavState = useMemo(() => { if (cutActive == null) return { hasPrev: false, hasNext: false }; const i = ALL.indexOf(cutActive); return { hasPrev: i > 0, hasNext: i < ALL.length - 1 }; }, [cutActive]);

    const mvItems = useMemo(() => Object.values(byMv).sort((a, b) => Number(a.tooth) - Number(b.tooth)).map((m) => ({ ...m, onRemove: () => removeMvByTooth(Number(m.tooth)) })), [byMv, removeMvByTooth]);
    const attItems = useMemo(() => Object.values(byAtt).sort((a, b) => Number(a.tooth) - Number(b.tooth)).map((m) => ({ ...m, onRemove: () => removeAttByTooth(Number(m.tooth)) })), [byAtt, removeAttByTooth]);
    const cutItems = useMemo(() => Object.values(byCut).sort((a, b) => Number(a.tooth) - Number(b.tooth)).map((m) => ({ ...m, onRemove: () => removeCutByTooth(Number(m.tooth)) })), [byCut, removeCutByTooth]);

    /* iter97.61 — Headless mode renders just the tabs + content (no
       card frame, no accordion trigger, no Next footer). Used on the
       doctor's case-submission wizard Step 4, which already provides the
       step frame.
       iter98.99au — Per user direction (2026-02-19), the colored 4px
       left-bar treatment is dropped. The `accent` prop is kept as a
       no-op for backward compatibility with callers; the accent map
       below is intentionally unused. */
    const accentBarClass = "";
    return (
        <section
            id={anchorId || undefined}
            className={
                headless
                    ? "scroll-mt-24"
                    : `scroll-mt-24 bg-card border-[0.5px] border-border rounded-[16px] overflow-hidden ${open ? "shadow-sm" : ""}`
            }
            data-testid="ptp-accordion"
            data-headless={headless ? "true" : "false"}
            data-open={!headless && open ? "true" : "false"}
            data-complete={isComplete ? "true" : "false"}
        >
            {/* Accordion trigger — hidden in headless mode.
                iter98.99ca/cb — header layout matches PlanSectionAccordion
                EXACTLY (small mono "01"/"02" left-pad number → completion
                icon → small uppercase tracked label → summary → caret),
                using the same `bg-secondary/30` light header bar as the
                Submission tab so all accordions across the case page
                read with one continuous typographic rhythm. */}
            {!headless && (
            <button
                type="button"
                onClick={toggleOpen}
                className={`w-full flex items-center gap-3 px-5 py-3 text-left border-border/60 hover:bg-teal-50/40 transition-colors group ${open ? "border-b" : ""}`}
                style={{ background: "linear-gradient(to right, rgba(20,184,166,0.05) 0%, transparent 55%)" }}
                data-testid="ptp-trigger"
                data-open={open ? "true" : "false"}
                aria-expanded={open}
            >
                <span className="font-display text-[13px] font-bold tabular-nums shrink-0 w-6 text-teal-700">
                    {String(number).padStart(2, "0")}
                </span>
                <span className="grid place-items-center w-5 h-5 shrink-0">
                    {isComplete
                        ? <CheckCircle size={15} weight="fill" className="text-emerald-500" />
                        : (summary && summary !== "Not authored")
                            ? <Warning size={14} weight="fill" className="text-amber-500" />
                            : <Circle size={11} weight="regular" className="text-border" />
                    }
                </span>
                <h3 className="text-[11px] font-bold tracking-[0.14em] uppercase text-teal-900/80 shrink-0">
                    Per-tooth plan
                </h3>
                {!open && summary && (
                    <span
                        className={`ml-3 text-[11px] truncate min-w-0 normal-case tracking-normal ${isComplete ? "text-muted-foreground/80" : "text-muted-foreground/60 italic"}`}
                        data-testid="ptp-summary"
                    >
                        {summary}
                    </span>
                )}
                <span className="ml-auto flex items-center gap-2 shrink-0">
                    {mvAuthored > 0 && (
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border-[0.5px] ${THEMES.mv.pillClass}`} data-testid="ptp-pill-mv">
                            <ToothIcon size={11} weight="duotone" /> {mvAuthored} moved
                        </span>
                    )}
                    {attCount > 0 && (
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border-[0.5px] ${THEMES.att.pillClass}`} data-testid="ptp-pill-att">
                            <PuzzlePiece size={11} weight="duotone" /> {attCount} att
                        </span>
                    )}
                    {cutCount > 0 && (
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border-[0.5px] ${THEMES.cut.pillClass}`} data-testid="ptp-pill-cut">
                            <Scissors size={11} weight="duotone" /> {cutCount} cuts
                        </span>
                    )}
                    {biteOn && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border-[0.5px] bg-muted text-muted-foreground border-border" data-testid="ptp-pill-bite">
                            <WaveSine size={11} weight="duotone" /> Ramps on
                        </span>
                    )}
                    <CaretDown size={13} weight="bold" className={`transition-transform text-teal-700 group-hover:scale-110 ${open ? "rotate-180" : ""}`} />
                </span>
            </button>
            )}

            {(headless || open) && (
                <div data-testid="ptp-body">
                    <TabNav active={tab} counts={{ mv: mvAuthored, att: attCount, cut: cutCount }} onSwitch={setTab} />

                    {/* TAB — MOVEMENTS */}
                    {tab === "mv" && (
                        <div className="p-4" data-testid="ptp-tab-mv-panel">
                            <p className="text-[12px] text-muted-foreground mb-3">Tap a tooth to author its prescribed movements. The doctor sees this as the authoritative plan.</p>
                            <Legend
                                items={[
                                    { cls: "bg-card border-border", label: "Not authored" },
                                    { cls: "bg-emerald-50 border-emerald-400", label: "Fully authored" },
                                    { cls: "bg-emerald-50/60 border-emerald-400", label: "Partial" },
                                ]}
                                hint="← → keys to navigate"
                            />
                            <ArchProgress
                                byTooth={byMv}
                                predicate={(m) => m && (m.dirs || []).length > 0}
                                theme={THEMES.mv}
                                testidPrefix="ptp-mv"
                            />
                            <ToothChart
                                byTooth={byMv}
                                mvActive={mvActive}
                                theme={THEMES.mv}
                                statusFn={mvState}
                                badgeFor={(m) => (m && (m.dirs || []).length > 0 && m.amount ? "✓" : null)}
                                onTap={openMv}
                            />
                            {mvActive != null && mvDraft && (
                                <MovementEditor
                                    tooth={mvActive}
                                    draft={mvDraft}
                                    setDraft={setMvDraft}
                                    navState={mvNavState}
                                    onPrev={() => navMv(-1)}
                                    onNext={() => navMv(1)}
                                    onClose={closeMv}
                                    onSave={saveMvAndNext}
                                    onRemove={removeMv}
                                    hasExisting={!!byMv[mvActive] && (byMv[mvActive].dirs || []).length > 0}
                                />
                            )}
                            <div className="mt-4 border-[0.5px] border-border rounded-[8px] overflow-hidden">
                                <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b-[0.5px] border-border">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground/70">Authored movements</span>
                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border-[0.5px] ${THEMES.mv.pillClass}`} data-testid="ptp-mv-summary-count">{mvAuthored} teeth</span>
                                </div>
                                <MvSummaryTable items={mvItems} />
                            </div>
                        </div>
                    )}

                    {/* TAB — ATTACHMENTS */}
                    {tab === "att" && (
                        <div className="p-4" data-testid="ptp-tab-att-panel">
                            <p className="text-[12px] text-muted-foreground mb-3">Tap a tooth to set its attachment size, shape, and purpose. Purple = assigned.</p>
                            <Legend
                                items={[
                                    { cls: "bg-card border-border", label: "Unassigned" },
                                    { cls: "bg-violet-50 border-violet-500", label: "Attachment assigned" },
                                ]}
                            />
                            <ArchProgress
                                byTooth={byAtt}
                                predicate={(m) => !!m}
                                theme={THEMES.att}
                                testidPrefix="ptp-att"
                            />
                            <ToothChart
                                byTooth={byAtt}
                                mvActive={attActive}
                                theme={THEMES.att}
                                statusFn={(m) => (m ? "done" : "empty")}
                                badgeFor={(m) => m?.shape ? m.shape.split(" ")[0].substring(0, 4) : null}
                                onTap={openAtt}
                            />
                            {attActive != null && attDraft && (
                                <AttachmentEditor
                                    tooth={attActive}
                                    draft={attDraft}
                                    setDraft={setAttDraft}
                                    navState={attNavState}
                                    onPrev={() => navAtt(-1)}
                                    onNext={() => navAtt(1)}
                                    onClose={closeAtt}
                                    onSave={saveAttAndNext}
                                    onRemove={removeAtt}
                                    hasExisting={!!byAtt[attActive]}
                                />
                            )}
                            <div className="mt-4 border-[0.5px] border-border rounded-[8px] overflow-hidden">
                                <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b-[0.5px] border-border">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground/70">Assigned attachments</span>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border-[0.5px] ${THEMES.att.pillClass}`} data-testid="ptp-att-summary-count">{attCount} teeth</span>
                                        {attCount > 0 && (
                                            <button onClick={clearAllAtt} className="text-[10px] text-muted-foreground hover:text-rose-600 hover:bg-rose-50 px-2 py-0.5 rounded transition-colors flex items-center gap-1" data-testid="ptp-att-clear-all">
                                                <ArrowsClockwise size={11} weight="duotone" /> Clear all
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <AttSummaryTable items={attItems} />
                            </div>
                        </div>
                    )}

                    {/* TAB — CUTS */}
                    {tab === "cut" && (
                        <div className="p-4" data-testid="ptp-tab-cut-panel">
                            <p className="text-[12px] text-muted-foreground mb-3">Tap a tooth to add a cut. Defaults to buccal — change position in the editor. Amber = cut assigned.</p>
                            <Legend
                                items={[
                                    { cls: "bg-card border-border", label: "No cut" },
                                    { cls: "bg-amber-50 border-amber-500", label: "Cut assigned" },
                                ]}
                            />
                            <ToothChart
                                byTooth={byCut}
                                mvActive={cutActive}
                                theme={THEMES.cut}
                                statusFn={(m) => (m ? "done" : "empty")}
                                badgeFor={(m) => m?.pos === "Lingual" ? "Ling" : "Bucc"}
                                onTap={openCut}
                            />
                            {cutActive != null && cutDraft && (
                                <CutEditor
                                    tooth={cutActive}
                                    draft={cutDraft}
                                    setDraft={setCutDraft}
                                    navState={cutNavState}
                                    onPrev={() => navCut(-1)}
                                    onNext={() => navCut(1)}
                                    onClose={closeCut}
                                    onSave={saveCutAndNext}
                                    onRemove={removeCut}
                                    hasExisting={!!byCut[cutActive]}
                                />
                            )}
                            <div className="mt-4 border-[0.5px] border-border rounded-[8px] overflow-hidden">
                                <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b-[0.5px] border-border">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground/70">Assigned precision cuts</span>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border-[0.5px] ${THEMES.cut.pillClass}`} data-testid="ptp-cut-summary-count">{cutCount} teeth</span>
                                        {cutCount > 0 && (
                                            <button onClick={clearAllCuts} className="text-[10px] text-muted-foreground hover:text-rose-600 hover:bg-rose-50 px-2 py-0.5 rounded transition-colors flex items-center gap-1" data-testid="ptp-cut-clear-all">
                                                <ArrowsClockwise size={11} weight="duotone" /> Clear all
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <CutSummaryTable items={cutItems} />
                            </div>
                            <div className="mt-3 flex items-start gap-1.5 p-2.5 rounded-[8px] text-[11px] leading-snug bg-amber-50 border-[0.5px] border-amber-400 text-amber-900">
                                <Info size={13} weight="duotone" className="mt-0.5 shrink-0" />
                                <p>Precision cuts default to buccal. Change in the editor above. These appear in the lab instruction sheet.</p>
                            </div>
                        </div>
                    )}

                    {/* TAB — BITE RAMPS */}
                    {tab === "bite" && (
                        <div className="p-4" data-testid="ptp-tab-bite-panel">
                            <BiteRampsPanel value={bite} onChange={setBite} />
                        </div>
                    )}

                    {/* Next footer — matches PlanSectionAccordion's stepper rhythm so this
                       section stays consistent with the other plan sections (Bite metrics,
                       Protocol, …). Only rendered when parent supplies an onNext callback. */}
                    {onNext && !headless && (
                        <div className="px-5 py-3 border-t border-border/30 bg-secondary/20 flex items-center justify-end gap-2">
                            <span className="text-[11px] text-muted-foreground mr-auto">
                                {isComplete ? "Looks good." : "You can come back to fill the rest later."}
                            </span>
                            <button
                                type="button"
                                onClick={onNext}
                                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card hover:bg-muted/60 px-3 py-1.5 text-[12px] font-medium transition-colors"
                                data-testid="ptp-next"
                            >
                                Next <CaretRight size={12} weight="bold" />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
